import { getProductsInProductImpressionFormat } from '@/helpers/format-product'
import {
  notifyExternalServices,
  ACTIONS,
  CONTEXTS,
} from '@/services/external-communication/external-communication'

import MUTATION_HELPERS from './card-mutation-helpers'
import FILTERING_HELPERS from './filtering-helpers'

export default {
  ...MUTATION_HELPERS,
  ...FILTERING_HELPERS,

  determineHiddenSecondaries(state, commit) {
    let hiddenSecondaries = []

    state.secondaryFilters.forEach(secondary => {
      let conditonsMet = []

      // Check if secondary filter meets at least one exclusion criteria
      Object.keys(secondary.excludeIf).forEach(condition => {
        state.activePrimaryFilters.forEach(activeFilter => {
          if (secondary.excludeIf[condition].includes(activeFilter[condition])) {
            conditonsMet.push(true)
          }
        })
      })

      // If so add filter to the hidden secondaries filters
      if (conditonsMet.length > 0) {
        hiddenSecondaries.push(secondary.id)
      }
    })
    commit('setHiddenSecondaries', hiddenSecondaries)
  },

  getCurrentSubPage(subPages, activeFilters) {
    // activeFilters and subPage must have same length && all filters must be equal
    let currentSubPage = subPages.filter(subPage => {
      if (subPage.filterMatches.length !== activeFilters.length) return false
      return activeFilters.every(filter => subPage.filterMatches.includes(filter))
    })

    // Fallback to the case with no selected filters if no matching subPage could be found
    return currentSubPage.length > 0 ? currentSubPage : this.getCurrentSubPage(subPages, [])
  },

  deepCopyVariants(state) {
    state.cardsFiltered.forEach(filteredCard => {
      let blueprintIndex = state.cards.findIndex(blueprint => blueprint.id === filteredCard.id)
      let blueprintVariants = JSON.parse(JSON.stringify(state.cards[blueprintIndex].variants))
      filteredCard.variants = blueprintVariants
    })
  },

  checkMandatoryFilterMatches(card, filterKey, filterValue) {
    if (!card.mandatoryFiltersNotMatched[filterKey]) return

    const filterIndex = card.mandatoryFiltersNotMatched[filterKey].indexOf(filterValue)
    if (filterIndex >= 0) {
      // Remove progressively the matching mandatory filters from the array of mandatory filters
      // that have not been matched yet. Later on, we will be able to filter out the cards that
      // still have filters in that array (meaning not all mandatory filters have been matched).
      card.mandatoryFiltersNotMatched[filterKey].splice(filterIndex, 1)
      card.priorityLevel++
    }
  },

  // REPORTING
  reportLoadingOfProducts(filteredCards) {
    const currency = document.body.dataset.currency
    const items = getProductsInProductImpressionFormat(filteredCards)

    if (filteredCards.length) {
      notifyExternalServices({
        action: ACTIONS.listView,
        productData: { items, currency },
      })
    }
  },

  reportPromoCardImpression(filteredCardsWithPromos) {
    const promoCards = filteredCardsWithPromos.filter(card => card.isPromoCard)
    const promotions = promoCards.map(card => {
      return { name: card.identifier, creative: card.imageUrl, position: CONTEXTS.promoCard }
    })

    if (promotions.length) {
      notifyExternalServices({
        action: ACTIONS.promotionView,
        promotions,
      })
    }
  },

  reportVirtPath() {
    const path = window.location.pathname
    const locale = document.body.dataset.locale
    notifyExternalServices({
      action: ACTIONS.virtPath,
      details: {
        virtPagePath: path.split(locale)[1],
        virtPageTitle: document.title,
      },
    })
  },

  // NAVIGATION
  composeUrl(state) {
    const concatenatePathFilters = (concatFilters, filterName) => {
      if (filterName !== '') concatFilters = concatFilters + filterName + '/'
      return concatFilters
    }

    const concatenateQueryFilters = (concatFilters, filter, currentIndex, array) => {
      // Add "?" symbol only once in the beginning and only if any queryFilters are present
      if (array.length !== 0 && currentIndex === 0) concatFilters = concatFilters + '?'
      concatFilters = concatFilters + Object.keys(filter)[0] + '=' + Object.values(filter)[0]
      if (currentIndex < array.length - 1) {
        concatFilters = concatFilters + '&'
      }
      return concatFilters
    }

    const concatenatedPathFilters = state.pathFilters
      .reduce(concatenatePathFilters, '')
      // Slice final "/" symbol
      .slice(0, -1)

    const concatenatedQueryFilters = state.queryFilters.reduce(concatenateQueryFilters, '')

    const sortSeparatorSymbol = concatenatedQueryFilters === '' ? '?' : '&'
    // Convention: don't show sorting in url if default sorting "featured" is set
    const sortPath =
      state.currentSorting !== 'featured'
        ? `${sortSeparatorSymbol}sort=${state.currentSorting}`
        : ''
    return `${state.basePath}${concatenatedPathFilters}${concatenatedQueryFilters}${sortPath}`
  },

  checkGeneralFilters(state) {
    // Loop all selected filters and save them according to if they belong in the path or in the query of the url
    let pathFilters = []
    let queryFilters = []
    let key = ''
    ;[state.itemFilters.selected, state.variantFilters.selected].forEach(filters => {
      Object.keys(filters).forEach(filterName => {
        state.allFilters.forEach(filter => {
          if (filters[filterName].length && filter.id === filterName) {
            // Collect all pathFilters but 'gender' and 'product'
            if (filter.includedInPath) {
              if (filterName !== 'gender' && filterName !== 'product') {
                filters[filterName].forEach(filterValue => pathFilters.push(filterValue))
              }
              // Collect queryFilters
            } else if (!filter.includedInPath) {
              filters[filterName].forEach(filterValue => {
                // Take care of special case 'size' => use key instead of value
                if (filterName.indexOf('size') > -1) {
                  key = Object.values(
                    filter.values.find(sizePair => Object.keys(sizePair)[0] === filterValue)
                  )[0]
                  // key = Object.values(filterPair)[0];
                } else {
                  key = filterValue
                }
                queryFilters.push({
                  [filterName]: key,
                })
              })
            }
          }
        })
      })
    })
    return { pathFilters, queryFilters }
  },

  checkGenderAndProductFilters(state, pathFilters) {
    // Add 'gender' and 'product' to the beginning of pathFilters array as they have to be displayed first in URL
    [state.itemFilters.selected['product'], state.variantFilters.selected['gender']].forEach(
      filter => {
        if (filter !== undefined && filter[0] !== '') {
          filter.forEach(filterValue => pathFilters.unshift(filterValue))
        }
      }
    )
    return pathFilters
  },

  getPathAndQueryFilters(state) {
    let filters = this.checkGeneralFilters(state)
    let pathFilters = this.checkGenderAndProductFilters(state, filters.pathFilters)
    return { pathFilters, queryFilters: filters.queryFilters }
  },

  setMaxSale(cards) {
    cards.forEach(card => {
      if (card.variants.length > 0) {
        const variantWithMostItemsSold = card.variants.reduce((prev, current) =>
          prev.soldItems > current.soldItems ? prev : current
        )
        card.maxSale = variantWithMostItemsSold.soldItems
        card.displayedVariantId = variantWithMostItemsSold.id
      }
    })
    return cards
  },

  resetDisplayedVariant(cards) {
    cards.forEach(card => (card.displayedVariantId = card.mainVariantId))
  },

  // Retrieve information for tags from selected item and variant filters
  createTagsFromFilters({ state, filters, tags, level }) {
    Object.keys(filters).forEach(key => {
      if (filters[key].length && filters[key][0] !== '') {
        filters[key].forEach(valueId => {
          // Make sure no tags for primary filters get created
          if (!this.isPrimaryFilter(state, key)) {
            tags.push({
              filterId: key,
              level: level,
              value: '',
              valueId: valueId,
            })
          }
        })
      }
    })
    return tags
  },

  // Filter values can't be extracted directly from selected item & variant filters -
  // we have to get it from the secondary filters
  setTagValues(state, tags) {
    tags.forEach(tag => {
      let secondary = state.secondaryFilters.filter(secondary => secondary.id === tag.filterId)
      secondary[0].values.forEach(value => {
        // Shoe sizes need special treatment due to different international sizing
        const valueToCompare =
          secondary[0].id.indexOf('size_shoes') > -1
            ? Object.keys(value)[0]
            : Object.values(value)[0]
        if (valueToCompare === tag.valueId) tag.value = Object.keys(value)[0]
      })
    })
    return tags
  },

  isPrimaryFilter(state, key) {
    return state.primaryFilters.find(filter => filter.id === key)
  },
}
